import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveSourceNav, sourceTypeLabel } from '@/lib/sourceNav';

/**
 * Reusable "View Source" drill-down action — the last hop from a
 * transaction / journal entry to its ORIGINAL source document.
 *
 * Reuses the existing destination routes/screens (no new pages). When the
 * source type has no existing destination it renders the reference as plain,
 * non-clickable text (or nothing), so it never crashes on an unknown source.
 *
 * Props:
 *   sourceType, sourceId  — preferred (canonical) resolution.
 *   reference             — display fallback only.
 *   compact               — small inline text link (for dense tables).
 */
export function ViewSourceLink({
  sourceType,
  sourceId,
  reference = '',
  compact = false,
  label = 'View Source',
  className = '',
}) {
  const navigate = useNavigate();
  const to = resolveSourceNav(sourceType, sourceId);

  if (!to) {
    return reference ? (
      <span className="text-xs text-muted-foreground" title={sourceTypeLabel(sourceType)} data-testid="view-source-plain">{reference}</span>
    ) : null;
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); navigate(to); }}
        className={`inline-flex items-center gap-1 text-xs text-primary hover:underline decoration-dotted underline-offset-2 ${className}`}
        title={`Open ${sourceTypeLabel(sourceType)}`}
        data-testid="view-source-link"
      >
        <ExternalLink className="w-3 h-3" /> {label}
      </button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={`gap-1.5 ${className}`}
      onClick={(e) => { e.stopPropagation(); navigate(to); }}
      title={`Open ${sourceTypeLabel(sourceType)}`}
      data-testid="view-source-btn"
    >
      <ExternalLink className="w-3.5 h-3.5" /> {label}
    </Button>
  );
}

export default ViewSourceLink;
